import { ConfirmDelete } from './ConfirmDelete/ConfirmDelete';
import * as Styled from './OrganizationBox.styled';
import MenuIcon from '../../../../assets/icons/MenuIcon.svg';
import { useCommonAuthProvider, LoginWithRedirectOptions } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { CopyToClipboard } from '@modules/shared/components/CopyToClipboard/CopyToClipboard';
import { useOrganization } from 'app/_providers/Organization';

import { Button, Dropdown, Tooltip, Row, Col } from 'antd';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { DisplayRole, OrgRole, OrgRoles } from 'shared/src/authz/rolesV2';
import { toDisplayRole, toOrgRole } from 'shared/src/authz/utils';
import { isOrgOwner } from 'shared/src/authz/validators';
import { Organization } from 'shared/src/organization/types';
import { Membership } from 'shared/src/types';

import type { MenuProps } from 'antd/lib/menu';

export const OrganizationBox = ({
  membership,
  fetchMemberships,
  handleShowOwners,
}: {
  membership: Membership;
  fetchMemberships: () => Promise<unknown>;
  handleShowOwners: (organization: Organization) => void;
}) => {
  const { isLoading, loginWithRedirect, user } = useCommonAuthProvider();
  const router = useRouter();

  const getRoleDisplay = () => {
    if (isOrgOwner(membership.roles)) {
      return DisplayRole.OWNER;
    }

    const firstRole = membership.roles[0];

    if (Object.values(OrgRole).includes(firstRole as OrgRole)) {
      return toDisplayRole(firstRole as OrgRole);
    }

    // Fallback: try to normalize to OrgRole and then get display role
    const normalizedRole = toOrgRole(firstRole as OrgRoles);

    return normalizedRole ? toDisplayRole(normalizedRole) : firstRole;
  };

  const role = getRoleDisplay();
  const { organizationId: tenantId } = useOrganization();
  const { cloudMspEnableUiFeatures } = useFlagsProxy();

  const handleOrganizationNavigation = (e: React.MouseEvent<HTMLSpanElement>): void => {
    e.stopPropagation();

    if (cloudMspEnableUiFeatures && membership.invalidSsoMembership) {
      router.push(`/migrate-sso-membership?returnTo=${membership.organizationId}`);
    } else {
      const loginArgs: LoginWithRedirectOptions = {
        organizationId: membership.tenant,
        returnTo: `/organizations/${membership.tenant}`,
      };

      if (membership.connections && membership.connections.length > 0) {
        loginArgs['connection'] = membership.connections[0];
        loginArgs['loginHint'] = user?.email;
      }

      loginWithRedirect(loginArgs);
    }
  };

  const handleViewOwnersClick: MenuProps['onClick'] = () => {
    handleShowOwners({
      ...membership.organization,
      organizationId: membership.tenant,
    });
  };

  return (
    <Styled.BoxMainRow
      align='middle'
      justify='center'
    >
      <Col xs={8} md={12} xl={16} onClick={handleOrganizationNavigation}>
        <Row>
          <Styled.SelectCol sm={3} md={3} lg={1}>
            {membership.tenant === tenantId && (
              <Tooltip
                placement='topRight'
                title='Current Organization'
              >
                <Styled.SelectedStarIconStyled />
              </Tooltip>
            )}
          </Styled.SelectCol>
          <Col xs={18} md={9}>
            <Styled.TitleText>{membership.organization?.organizationAlias || membership.tenant}</Styled.TitleText>
            <Styled.IdText>
              {membership.tenant}
              <Styled.IconWrapper>
                <CopyToClipboard textToCopy={membership.tenant} />
              </Styled.IconWrapper>
            </Styled.IdText>
          </Col>
        </Row>
      </Col>

      <Col
        xs={8}
        md={6}
        xl={4}
        style={{ textAlign: 'center' }}
        onClick={handleOrganizationNavigation}
      >
        <Styled.UserIconStyled roles={membership.roles} />

        <Styled.RoleText>{role}</Styled.RoleText>
      </Col>

      <Col xs={8} md={6} xl={4}>
        <Row
          align='middle'
          style={{ justifyContent: 'end' }}
        >
          <Button
            data-testid='launch-button'
            type='default'
            onClick={handleOrganizationNavigation}
          >
            Launch
          </Button>

          <Dropdown
            menu={
              {
                items: [
                  {
                    key: 'owners',
                    onClick: handleViewOwnersClick,
                    label: 'View Owners',
                  },
                  {
                    danger: true,
                    key: 'deleteOrganization',
                    label: (
                      <ConfirmDelete
                        organizationId={membership.tenant}
                        role={(role && toOrgRole(role)) ?? OrgRole.USER}
                        organizationAlias={membership.organization.organizationAlias}
                        fetchMemberships={fetchMemberships}
                      />
                    ),
                  },
                ],
              }
            }
            trigger={['click']}
          >
            <Button
              loading={isLoading}
              icon={<MenuIcon />}
              type='text'
              data-testid={`org-menu-${membership.tenant}`}
            />
          </Dropdown>
        </Row>
      </Col>
    </Styled.BoxMainRow>
  );
};
